import type { GroupId, GuildBuildEntry, Player, PlayerGroup, Room } from '../../types'
import { parseAccent, parsePosition, parseRole, parseTag } from '../../lib/boardInvariants'

export interface RoomNode {
  readonly id: number
  readonly stableId: string
  readonly name: string
  readonly accent: string
  readonly noPlayerLimit: boolean
  readonly orderIndex: number
}

export interface GroupNode {
  readonly id: number
  readonly stableId: string
  readonly roomId: number
  readonly label: string
  readonly position: string
  readonly maxPlayers: number
  readonly collapsed: boolean
  readonly orderIndex: number
}

export interface PlayerWithBuildsNode {
  readonly player: {
    readonly id: number
    readonly discordUserId: string
    readonly hasSignedIn: boolean
    readonly discordName: string
    readonly guildNickname: string
    readonly playerNickname: string
    readonly playerAccountName: string
    readonly inGameName: string
    readonly role: string
    readonly groupId: number
    readonly groupOrderIndex: number
    readonly mainWeapons: ReadonlyArray<string>
    readonly altWeapons: ReadonlyArray<string>
    readonly activeBuildIndex: number
    readonly activeGuildBuildIndex: number
    readonly tags: ReadonlyArray<{
      readonly id: number
      readonly guildId: number
      readonly name: string
      readonly seedKey: string | null
      readonly color: string
    }>
  }
  readonly builds: ReadonlyArray<{
    readonly id: number
    readonly orderIndex: number
    readonly weaponSlugs: ReadonlyArray<string>
  }>
  readonly guildBuilds?: ReadonlyArray<{
    readonly id: number
    readonly orderIndex: number
    readonly weaponSlugs: ReadonlyArray<string>
    readonly isAssigned: boolean
  }>
}

export interface BoardPayload {
  readonly guildPlayerDiscordIds?: ReadonlyArray<string> | null
  board?: {
    rooms: ReadonlyArray<RoomNode>
    groups: ReadonlyArray<GroupNode>
    players: ReadonlyArray<PlayerWithBuildsNode>
  }
}

export function mapRoom(n: RoomNode): Room {
  return {
    id: n.stableId || String(n.id),
    name: n.name,
    groupIds: [],
    accent: parseAccent(n.accent),
    noPlayerLimit: n.noPlayerLimit,
  }
}

export function mapGroup(n: GroupNode): PlayerGroup {
  return {
    id: n.stableId || String(n.id),
    label: n.label,
    position: parsePosition(n.position),
    playerIds: [],
    maxPlayers: n.maxPlayers,
    collapsed: n.collapsed,
  }
}

export function mapPlayer(n: PlayerWithBuildsNode, groupId: GroupId): Player {
  const builds = [...n.builds]
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((b) => [...b.weaponSlugs])
  const guildBuilds = [...(n.guildBuilds ?? [])]
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((b): GuildBuildEntry => ({
      id: String(b.id),
      orderIndex: b.orderIndex,
      weaponSlugs: [...b.weaponSlugs],
      isAssigned: b.isAssigned,
    }))
  const tags = n.player.tags.map(parseTag)
  return {
    id: String(n.player.id),
    discordUserId: String(n.player.discordUserId),
    hasSignedIn: n.player.hasSignedIn,
    discordName: n.player.discordName,
    guildNickname: n.player.guildNickname,
    playerNickname: n.player.playerNickname,
    playerAccountName: n.player.playerAccountName,
    inGameName: n.player.inGameName,
    role: parseRole(n.player.role),
    group: groupId,
    mainWeapons: [...n.player.mainWeapons],
    altWeapons: [...n.player.altWeapons],
    builds,
    guildBuilds,
    activeBuildIndex: n.player.activeBuildIndex,
    activeGuildBuildIndex: n.player.activeGuildBuildIndex,
    tags,
  }
}
